<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:760px">
      <h3 class="mb-1">Tjenestekontoer</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        Maskin-kontoer med minst-privilegium som frontend-brokerne logger inn med. Passordet lagres
        <strong>kryptert</strong> på bruker-docen i Couchbase; å lagre her <strong>oppretter eller roterer</strong>
        kontoen (brukernavn er låst).
      </div>

      <div class="alert alert-warning mt-3 py-2" style="font-size:0.88rem">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <strong>Delt hemmelighet:</strong> hvert passord må <strong>matche</strong> frontend-miljøvariabelen
        (vist per konto under). Roterer du her uten å oppdatere frontend-env, slutter den brokeren å logge inn.
      </div>

      <config-lock ref="lock" :configured="isConfigured">
      <div v-for="acc in accounts" :key="acc.key" class="sa-card">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap:0.5rem">
          <div>
            <span class="fw-semibold">{{ acc.label }}</span>
            <span class="badge ms-2" :class="acc.exists ? 'bg-success' : 'bg-secondary'">
              {{ acc.exists ? 'Konfigurert ✓' : 'Ikke satt' }}
            </span>
          </div>
          <span class="badge bg-body-tertiary text-body" style="font-weight:500">{{ acc.role }}</span>
        </div>
        <!-- The role the code declares and the role stored on the user document can disagree: setting a
             password only ever updates the password, so a rename in the code does nothing to an account
             that already exists. Silent until something 403s, hence said out loud. -->
        <div v-if="acc.exists && acc.storedRole && acc.storedRole !== acc.role"
             class="mt-2" style="font-size:0.82rem; color:#B45309;">
          Lagret rolle er <code>{{ acc.storedRole }}</code>, koden forventer <code>{{ acc.role }}</code>.
          Trykk «Synkroniser roller» nederst.
        </div>
        <div>
        </div>
        <div style="color:var(--bo-muted); font-size:0.8rem" class="mt-1">
          Brukernavn: <code>{{ acc.username }}</code> · frontend-env: <code>{{ acc.envVar }}</code>
        </div>
        <div class="d-flex align-items-center gap-2 mt-2 flex-wrap">
          <div style="position:relative; flex:1 1 260px; max-width:340px">
            <input :type="acc.show ? 'text' : 'password'" class="form-control form-control-sm" style="padding-right:38px"
                   v-model="acc.password" autocomplete="new-password"
                   :placeholder="acc.exists ? 'Nytt passord (roter)' : 'Sett passord'" />
            <button type="button" @click="acc.show = !acc.show" :title="acc.show ? 'Skjul' : 'Vis'"
                    style="position:absolute; right:10px; top:50%; transform:translateY(-50%); border:none; background:none; color:var(--bo-muted); cursor:pointer; padding:0; line-height:1;">
              <font-awesome-icon :icon="['fas', acc.show ? 'eye-slash' : 'eye']" />
            </button>
          </div>
          <button class="bo_btn_secondary" style="min-width:auto; font-size:0.82rem; padding:4px 12px"
                  :disabled="acc.saving || !acc.password" @click="save(acc)">
            {{ acc.saving ? 'Lagrer…' : (acc.exists ? 'Roter passord' : 'Opprett') }}
          </button>
          <span v-if="acc.message" :class="acc.error ? 'text-danger' : 'text-success'" style="font-size:0.85rem">{{ acc.message }}</span>
        </div>
      </div>

      <!-- Outside each card because it reconciles all of them at once, and inside the lock because it
           writes. Safe to press at any time: it only writes where the two disagree. -->
      <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
        <button class="bo_btn_secondary" style="min-width:auto; font-size:0.82rem; padding:4px 12px"
                :disabled="reconciling" @click="reconcile">
          {{ reconciling ? 'Synkroniserer…' : 'Synkroniser roller' }}
        </button>
        <span style="color:var(--bo-muted); font-size:0.8rem">
          Setter lagret rolle lik den koden forventer. Endrer ikke passord eller brukernavn.
        </span>
      </div>
      <div v-if="reconcileMessage" class="mt-2" :class="reconcileError ? 'text-danger' : 'text-success'"
           style="font-size:0.85rem">{{ reconcileMessage }}</div>
      </config-lock>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import SettingsService from "@/services/settings/SettingsService";

export default {
  name: "ServiceAccounts",
  components: { LoadingSpinner, ConfigLock },
  computed: {
    // Already configured (→ starts locked): at least one service-account password is already set.
    isConfigured() { return this.accounts.some((a) => a.exists); }
  },
  data() {
    return { loading: true, accounts: [], reconciling: false, reconcileMessage: "", reconcileError: false };
  },
  mounted() {
    this.load();
  },
  methods: {
    reconcile() {
      this.reconciling = true;
      this.reconcileMessage = "";
      this.reconcileError = false;
      SettingsService.reconcileServiceAccountRoles().then(
        (response) => {
          const rows = (response.data && response.data.result) || [];
          const changed = rows.filter((r) => r.result === 'OPPDATERT');
          const failed = rows.filter((r) => r.result === 'FEILET');
          this.reconcileError = failed.length > 0;
          this.reconcileMessage = failed.length
            ? 'Feilet for: ' + failed.map((r) => r.username).join(', ')
            : (changed.length
                ? 'Oppdatert: ' + changed.map((r) => r.username + ' (' + r.detail + ')').join(', ')
                : 'Alle roller stemte allerede.');
          this.reconciling = false;
          this.load();
        },
        (error) => {
          console.log(error);
          this.reconcileError = true;
          this.reconcileMessage = 'Kunne ikke synkronisere rollene.';
          this.reconciling = false;
        }
      );
    },
    load() {
      SettingsService.getServiceAccounts().then(
        (response) => {
          const list = (response.data && response.data.accounts) || [];
          this.accounts = list.map((a) => ({ ...a, password: "", show: false, saving: false, message: "", error: false }));
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
        }
      );
    },
    save(acc) {
      acc.saving = true;
      acc.message = "";
      acc.error = false;
      SettingsService.setServiceAccountPassword(acc.key, acc.password).then(
        (response) => {
          acc.saving = false;
          acc.password = "";
          acc.show = false;
          acc.error = false;
          acc.message = "Lagret ✓ — husk frontend-env.";
          const fresh = ((response.data && response.data.accounts) || []).find((x) => x.key === acc.key);
          if (fresh) acc.exists = fresh.exists;
          this.$refs.lock.relock();   // re-lock after a successful save
        },
        (error) => {
          acc.saving = false;
          acc.error = true;
          const data = error && error.response ? error.response.data : null;
          acc.message = (data && data.error) || "Kunne ikke lagre.";
        }
      );
    }
  }
};
</script>

<style scoped>
.sa-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  margin-top: 0.75rem;
}
</style>
